<script lang="ts">
import type { ProductImage } from '@convatec/180medical-website/types/product-image'
import { getPrimaryImageIndex } from '~/utils/getPrimaryImage'

export interface ImageGalleryBlockProps {
  images: ProductImage[]
}
</script>

<script setup lang="ts">
const props = defineProps<ImageGalleryBlockProps>()

// -1 (no images) can't reach here — the template's v-if guards it. The
// assertion reflects what TS can't see across that template boundary.
const selectedIndex = ref(getPrimaryImageIndex(props.images))
const selectedImage = computed(() => props.images[selectedIndex.value]!)

// Function to select an image
function selectImage(index: number) {
  selectedIndex.value = index
}
</script>

<template>
  <div
    v-if="images?.length"
    class="w-full sticky top-4"
  >
    <!-- Selected/large image display -->
    <div class="mb-4 overflow-hidden rounded-lg shadow-xl">
      <NuxtImg
        provider="fastlyAprimo"
        :src="selectedImage.url"
        :alt="selectedImage.altText"
        class="w-full h-auto aspect-square object-contain"
      />
    </div>

    <!-- Thumbnail grid -->
    <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
      <button
        v-for="(image, index) in images"
        :key="index"
        class="cursor-pointer overflow-hidden rounded-md shadow-lg/15 transition-all duration-200"
        :class="{ 'ring-2 ring-med-aqua ring-offset-1': selectedIndex === index }"
        :aria-label="`View ${image.altText ? image.altText : 'image'}`"
        @click="selectImage(index)"
      >
        <NuxtImg
          provider="fastlyAprimo"
          class="w-full aspect-square object-contain hover:opacity-90 transition-opacity"
          :src="image.url"
          :alt="image.altText"
        />
      </button>
    </div>
  </div>
</template>
